import React, { useState } from 'react';

const COLORS = ['#38bdf8','#818cf8','#34d399','#fb923c','#f472b6','#a78bfa','#fbbf24','#2dd4bf','#f87171','#e879f9'];

export default function TablesView({ data }) {
  const [active, setActive] = useState(null);
  if (!data) return <div style={{ padding: 40, color: 'var(--muted)', fontSize: 12 }}>Select a dataset to view tables.</div>;

  const tables = Array.isArray(data) ? data : (data.tables || []);

  return (
    <div style={{ height: '100%', overflow: 'auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
        <h2 style={{ fontFamily: 'Syne, sans-serif', fontSize: 14, fontWeight: 700, letterSpacing: '.05em' }}>Tables</h2>
        <span className="tag">{tables.length} tables</span>
      </div>

      {/* Top-level metadata */}
      {!Array.isArray(data) && (
        <div className="card" style={{ marginBottom: 20, fontSize: 11 }}>
          {data.dataset_name && <div style={{ fontFamily: 'Syne, sans-serif', fontWeight: 700, fontSize: 13, marginBottom: 6, color: 'var(--accent)' }}>{data.dataset_name}</div>}
          {data.description && <div style={{ color: 'var(--muted)', marginBottom: 8 }}>{data.description}</div>}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {data.tables_count && <span className="tag">tables: {data.tables_count}</span>}
            {(data.code_systems_mentioned || []).map(cs => <span key={cs} className="tag">{cs}</span>)}
          </div>
        </div>
      )}

      {/* Relationships summary */}
      {data.relationships?.length > 0 && (
        <div className="card" style={{ marginBottom: 20 }}>
          <div style={{ fontFamily: 'Syne, sans-serif', fontWeight: 700, fontSize: 12, marginBottom: 10, letterSpacing: '.05em' }}>
            RELATIONSHIPS <span className="tag" style={{ marginLeft: 6 }}>{data.relationships.length}</span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {data.relationships.map((r, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 11 }}>
                <span style={{ color: 'var(--accent)', fontWeight: 600 }}>{r.from_table}</span>
                <span style={{ color: 'var(--muted)', fontSize: 9 }}>({r.from_column})</span>
                <span style={{ color: 'var(--fk)' }}>→</span>
                <span style={{ color: 'var(--accent2)', fontWeight: 600 }}>{r.to_table}</span>
                <span style={{ color: 'var(--muted)', fontSize: 9 }}>({r.to_column})</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Table cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px,1fr))', gap: 14 }}>
        {tables.map((t, i) => {
          const name = t.table_name || t.name || `table_${i}`;
          const cols = t.columns || t.cols || [];
          const isOpen = active === name;
          const color = COLORS[i % COLORS.length];
          return (
            <div key={name} className="card" style={{ padding: 0, overflow: 'hidden', cursor: 'pointer' }}
              onClick={() => setActive(isOpen ? null : name)}>
              <div style={{ padding: '12px 16px', borderLeft: `3px solid ${color}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                  <div style={{ fontFamily: 'Syne, sans-serif', fontWeight: 700, fontSize: 12, color: isOpen ? color : 'var(--text)', letterSpacing: '.03em' }}>{name}</div>
                  {t.description && <div style={{ fontSize: 10, color: 'var(--muted)', marginTop: 2 }}>{t.description}</div>}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span className="tag">{cols.length} cols</span>
                  <span style={{ color: 'var(--muted)', fontSize: 12 }}>{isOpen ? '▲' : '▼'}</span>
                </div>
              </div>

              {isOpen && (
                <div style={{ borderTop: '1px solid var(--border)' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                    <thead>
                      <tr style={{ background: 'var(--row-overlay)' }}>
                        {['', 'Column', 'Type', 'Notes'].map(h => (
                          <th key={h} style={{ padding: '7px 12px', textAlign: 'left', fontSize: 9, color: 'var(--muted)', letterSpacing: '.06em', fontWeight: 700, borderBottom: '1px solid var(--border)' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {cols.map((c, ci) => {
                        const cname = c.name || c.n || '';
                        const ctype = c.data_type || c.type || c.t || '';
                        const isPK  = c.pk || false;
                        const isFK  = !!(c.relationship_hint || c.fk);
                        const note  = c.description || c.relationship_hint || '';
                        return (
                          <tr key={ci} style={{ borderBottom: '1px solid var(--border)' }}>
                            <td style={{ padding: '5px 12px', width: 30 }}>
                              {isPK && <span style={{ fontSize: 8, padding: '1px 4px', borderRadius: 3, background: 'rgba(251,191,36,.12)', color: '#fbbf24', border: '1px solid rgba(251,191,36,.3)', fontWeight: 700 }}>PK</span>}
                              {isFK && <span style={{ fontSize: 8, padding: '1px 4px', borderRadius: 3, background: 'rgba(244,114,182,.1)', color: '#f472b6', border: '1px solid rgba(244,114,182,.25)', fontWeight: 700 }}>FK</span>}
                            </td>
                            <td style={{ padding: '5px 12px', color: 'var(--text)', fontWeight: (isPK||isFK) ? 600 : 400 }}>{cname}</td>
                            <td style={{ padding: '5px 12px', color: 'var(--muted)', fontSize: 10 }}>{ctype}</td>
                            <td style={{ padding: '5px 12px', color: 'var(--muted)', fontSize: 10, maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{note}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
